import React, { useCallback, useEffect, useMemo } from 'react'
import * as yup from 'yup'
import { add, isValid } from 'date-fns'
import { GhgConstants, LivestockClass, LivestockClassType } from 'api/models'
import { Alert, Button, ButtonGroup, Form, InputGroup, Table } from 'react-bootstrap'
import { Control, Controller, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { DateInput } from './base/form/DateInput'
import { NumberField } from './base/form/NumberField'
import { GhgCalculatorNavigation } from './GhgCalculatorNavigation'
import {
  makeDefaultGrazingOffItem,
  makeDefaultGrazingOnItem,
  RegisterFormFn,
  useGhgCalculatorFormContext,
} from './use-ghg-calculator-form'
import lodashGet from 'lodash/get'
import isEqual from 'lodash/isEqual'
import { formatPercentage } from 'utils/formatting'
import { FormValuesWatcher } from './FormValuesWatcher'

import './GhgGrazingMovementsStep.scss'
import { useYupValidationResolver } from 'hooks/use-yup-validation-resolver'
import { requiredMsg, yupNullableMinZeroInteger } from 'utils/validation'
import { ErrorMessage, fieldErrorCssClass } from './base/form/ErrorMessage'
import { formatDateForDisplay, yupDateTransform } from 'utils/date'
import {
  useGrazingOffTimeOnFarmCalculation,
  useGrazingOnTimeOnFarmCalculation,
} from './use-ghg-calculation'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons'
import {
  FarmDetailState,
  GrazingOffMovementItemFrontend,
  GrazingOnMovementItemFrontend,
} from 'components/ghg-calculator.state'
import { getSeasonRange } from 'utils/season'

export const GhgGrazingMovementsStep = () => {
  const {
    ghgConstants,
    farmDetail,
    livestockClasses,
    livestockClassTypes,
    grazingOnMovements,
    grazingOffMovements,
    setGrazingOnMovements,
    setGrazingOffMovements,
    registerForm,
  } = useGhgCalculatorFormContext()

  if (!(livestockClasses && livestockClassTypes && ghgConstants)) {
    return null
  }
  return (
    <GhgGrazingMovementsStepInner
      farmDetail={farmDetail}
      livestockClasses={livestockClasses}
      livestockClassTypes={livestockClassTypes}
      grazingOnMovements={grazingOnMovements}
      grazingOffMovements={grazingOffMovements}
      setGrazingOnMovements={setGrazingOnMovements}
      setGrazingOffMovements={setGrazingOffMovements}
      ghgConstants={ghgConstants}
      registerForm={registerForm}
    />
  )
}

const DATE_OUTSIDE_SEASON_MSG = 'The date entered falls outside the analysis season.'

const tallyOpenValidation = (label: string) =>
  yup.number().when(['classification', 'on_hand_at_open'], {
    is: (classification: number, on_hand_at_open: boolean) => classification && !on_hand_at_open,
    then: yupNullableMinZeroInteger().required(requiredMsg(label)),
    otherwise: yupNullableMinZeroInteger().notRequired(),
  })

const tallyCloseValidation = (label: string) =>
  yup.number().when(['classification', 'on_hand_at_close'], {
    is: (classification: number, on_hand_at_close: boolean) => classification && !on_hand_at_close,
    then: yupNullableMinZeroInteger().required(requiredMsg(label)),
    otherwise: yupNullableMinZeroInteger().notRequired(),
  })

const dateOpenValidation = (label: string, seasonStart: Date, seasonEnd: Date) =>
  yup.date().when(['classification', 'on_hand_at_open'], {
    is: (classification: number, on_hand_at_open: boolean) => classification && !on_hand_at_open,
    then: yup
      .date()
      .transform(yupDateTransform)
      .nullable()
      .min(seasonStart, DATE_OUTSIDE_SEASON_MSG)
      .max(seasonEnd, DATE_OUTSIDE_SEASON_MSG)
      .required(requiredMsg(label))
      .typeError('Invalid date.'),
    otherwise: yup.date().transform(yupDateTransform).nullable(),
  })

const dateCloseValidation = (
  label: string,
  seasonStart: Date,
  seasonEnd: Date,
  dateOpenField: string
) =>
  yup.date().when(['classification', 'on_hand_at_close'], {
    is: (classification: number, on_hand_at_close: boolean) => classification && !on_hand_at_close,
    then: yup
      .date()
      .transform(yupDateTransform)
      .when(dateOpenField, (dateOpen: Date | null, schema: yup.DateSchema) => {
        if (dateOpen && isValid(dateOpen)) {
          return schema.min(
            add(dateOpen, { days: 1 }),
            `${label} must be after ${formatDateForDisplay(dateOpen)}`
          )
        }

        return schema.min(seasonStart, DATE_OUTSIDE_SEASON_MSG)
      })
      .nullable()
      .max(seasonEnd, DATE_OUTSIDE_SEASON_MSG)
      .required(requiredMsg(label))
      .typeError('Invalid date.'),
    otherwise: yup.date().transform(yupDateTransform).nullable(),
  })

interface GrazingOnFormValues {
  onItems: GrazingOnMovementItemFrontend[]
  offItems: GrazingOffMovementItemFrontend[]
}

const GhgGrazingMovementsStepInner = (props: {
  farmDetail: FarmDetailState
  livestockClasses: LivestockClass[]
  livestockClassTypes: LivestockClassType[]
  ghgConstants: GhgConstants
  grazingOnMovements: GrazingOnMovementItemFrontend[]
  grazingOffMovements: GrazingOffMovementItemFrontend[]
  setGrazingOnMovements: (onItems: GrazingOnMovementItemFrontend[]) => void
  setGrazingOffMovements: (offItems: GrazingOffMovementItemFrontend[]) => void
  registerForm: RegisterFormFn
}) => {
  const {
    farmDetail,
    grazingOnMovements,
    grazingOffMovements,
    setGrazingOnMovements,
    setGrazingOffMovements,
    registerForm,
  } = props

  const [seasonStart, seasonEnd] = useMemo(() => {
    if (!farmDetail.season_year) {
      throw new Error('[GhgGrazingMovementsStep] farmDetail.season_year should not be empty')
    }

    return getSeasonRange(farmDetail.season_year, farmDetail.end_month)
  }, [farmDetail])

  const validationSchema = useMemo(
    () =>
      yup.object({
        onItems: yup.array().of(
          yup.object({
            classification: yup
              .number()
              .required(requiredMsg('Livestock class'))
              .typeError(requiredMsg('Livestock class')),
            on_hand_at_open: yup.boolean(),
            on_hand_at_close: yup.boolean(),
            tally_arrive: tallyOpenValidation('Tally arrive'),
            tally_leave: tallyCloseValidation('Tally leave'),
            arrived_date: dateOpenValidation('Date arrived', seasonStart, seasonEnd),
            left_date: dateCloseValidation('Date left', seasonStart, seasonEnd, 'arrived_date'),
          } as Record<keyof GrazingOnMovementItemFrontend, any>) // eslint-disable-line @typescript-eslint/no-explicit-any
        ),
        offItems: yup.array().of(
          yup.object({
            classification: yup
              .number()
              .required(requiredMsg('Livestock class'))
              .typeError(requiredMsg('Livestock class')),
            on_hand_at_open: yup.boolean(),
            on_hand_at_close: yup.boolean(),
            tally_depart: tallyOpenValidation('Tally depart'),
            tally_return: tallyCloseValidation('Tally return'),
            departed_date: dateOpenValidation('Date departed', seasonStart, seasonEnd),
            returned_date: dateCloseValidation(
              'Date returned',
              seasonStart,
              seasonEnd,
              'departed_date'
            ),
          } as Record<keyof GrazingOffMovementItemFrontend, any>) // eslint-disable-line @typescript-eslint/no-explicit-any
        ),
      }),
    [seasonStart, seasonEnd]
  )

  const resolver = useYupValidationResolver(validationSchema)

  const formBag = useForm<GrazingOnFormValues>({
    mode: 'all',
    resolver,
    defaultValues: {
      onItems: grazingOnMovements,
      offItems: grazingOffMovements,
    },
    shouldUnregister: false,
  })

  const { control, formState, handleSubmit } = formBag

  useEffect(() => {
    registerForm(formBag)
    return () => registerForm(null)
  }, [formBag, registerForm])

  const {
    fields: onFarmGrazingRows,
    append: appendOnFarmGrazing,
    remove: removeOnFarmGrazing,
  } = useFieldArray<GrazingOnMovementItemFrontend>({
    control,
    name: 'onItems',
  })

  const {
    fields: offFarmGrazingRows,
    append: appendOffFarmGrazing,
    remove: removeOffFarmGrazing,
  } = useFieldArray<GrazingOffMovementItemFrontend>({
    control,
    name: 'offItems',
  })

  const handleValuesChanged = useCallback(
    ({
      onItems,
      offItems,
    }: {
      onItems: GrazingOnMovementItemFrontend[]
      offItems: GrazingOffMovementItemFrontend[]
    }) => {
      setGrazingOnMovements(onItems)
      setGrazingOffMovements(offItems)
    },
    [setGrazingOnMovements, setGrazingOffMovements]
  )

  // formState is wrapped with Proxy to improve render performance, so make sure you invoke or read it
  // before render in order to enable the state update.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { errors, touched } = formState

  return (
    <div>
      {formState.isSubmitted && Object.keys(errors).length > 0 && (
        <div className="mt-4">
          <span className="text-danger">Please correct the errors below to continue.</span>
        </div>
      )}
      <legend className="section-heading">On-farm grazing</legend>
      <Alert variant="info">
        <p>
          ‘On-farm grazing’ refers to animals this business does not own that are brought onto your
          farm for only part of their lifespan – such as dairy heifers, dairy cows over winter, or
          lambs finished on contract. If you have no on-farm grazing, click the{' '}
          <FontAwesomeIcon icon={faTimesCircle} aria-labelledby="title" title="Remove" /> button to
          remove this section.
        </p>
      </Alert>

      <Table
        role="grid"
        data-testid="grazing-on-table"
        className="table-custom table-responsive table-grazing-movements"
      >
        <thead>
          <tr>
            <th scope="col">Stock on</th>
            <th scope="col" className="text-center">
              Tally arrive
            </th>
            <th scope="col" className="text-center">
              Tally leave
            </th>
            <th scope="col" className="text-center">
              On hand at open
            </th>
            <th scope="col" className="text-center">
              Date arrived
            </th>
            <th scope="col" className="text-center">
              On hand at close
            </th>
            <th scope="col" className="text-center">
              Date left
            </th>
            <th scope="col" className="text-center">
              Time on farm
            </th>
            <th scope="col" className="text-center">
              <span className="sr-only">Action</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {onFarmGrazingRows.map((row, idx) => (
            <OnFarmGrazingItemRow
              key={row.id}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              initalValue={row as any}
              itemIdx={idx}
              control={control}
              fieldPrefix={`onItems[${idx}]`}
              livestockClasses={props.livestockClasses}
              livestockClassTypes={props.livestockClassTypes}
              onRemove={removeOnFarmGrazing}
              seasonStart={seasonStart}
              seasonEnd={seasonEnd}
              rowErrors={{ ...lodashGet(errors, `onItems[${idx}]`) }}
              rowTouched={{ ...lodashGet(touched, `onItems[${idx}]`) }}
              isSubmitted={formState.isSubmitted}
            />
          ))}
        </tbody>
      </Table>
      <ButtonGroup>
        <Button
          variant="secondary"
          onClick={() => appendOnFarmGrazing(makeDefaultGrazingOnItem())}
          data-testid="add-one-grazing-on-item"
        >
          [+1] Add 1 <span className="sr-only">table row</span>
        </Button>
        <Button
          variant="secondary"
          onClick={() =>
            [1, 2, 3, 4, 5].forEach(() => appendOnFarmGrazing(makeDefaultGrazingOnItem()))
          }
          data-testid="add-five-grazing-on-item"
        >
          [+5] Add 5 <span className="sr-only">table rows</span>
        </Button>
      </ButtonGroup>

      <legend className="section-heading">Off-farm grazing</legend>
      <Alert variant="info">
        <p>
          ‘Off-farm grazing’ refers to animals this business owns that spend their main productive
          life on your farm, but which have been sent to another farm to graze – such as ewes sent
          for grazing in a drought or home-bred lambs being finished elsewhere on contract. If this
          doesn't apply to any of your stock, click the{' '}
          <FontAwesomeIcon icon={faTimesCircle} aria-labelledby="title" title="Remove" /> button to
          remove this section.
        </p>
      </Alert>

      <Table
        role="grid"
        data-testid="grazing-off-table"
        className="table-custom table-responsive table-grazing-movements"
      >
        <thead>
          <tr>
            <th scope="col">Stock off</th>
            <th scope="col" className="text-center">
              Tally depart
            </th>
            <th scope="col" className="text-center">
              Tally return
            </th>
            <th scope="col" className="text-center">
              Off farm at open
            </th>
            <th scope="col" className="text-center">
              Date departed
            </th>
            <th scope="col" className="text-center">
              Off farm at close
            </th>
            <th scope="col" className="text-center">
              Date returned
            </th>
            <th scope="col" className="text-center">
              Time on farm
            </th>
            <th scope="col" className="text-center">
              <span className="sr-only">Action</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {offFarmGrazingRows.map((row, idx) => (
            <OffFarmGrazingItemRow
              key={row.id}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              initalValue={row as any}
              itemIdx={idx}
              control={control}
              fieldPrefix={`offItems[${idx}]`}
              livestockClasses={props.livestockClasses}
              livestockClassTypes={props.livestockClassTypes}
              onRemove={removeOffFarmGrazing}
              seasonStart={seasonStart}
              seasonEnd={seasonEnd}
              rowErrors={{ ...lodashGet(errors, `offItems[${idx}]`) }}
              rowTouched={{ ...lodashGet(touched, `offItems[${idx}]`) }}
              isSubmitted={formState.isSubmitted}
            />
          ))}
        </tbody>
      </Table>
      <ButtonGroup>
        <Button
          variant="secondary"
          onClick={() => appendOffFarmGrazing(makeDefaultGrazingOffItem())}
          data-testid="add-one-grazing-off-item"
        >
          [+1] Add 1 <span className="sr-only">table row</span>
        </Button>
        <Button
          variant="secondary"
          onClick={() =>
            [1, 2, 3, 4, 5].forEach(() => appendOffFarmGrazing(makeDefaultGrazingOffItem()))
          }
          data-testid="add-five-grazing-off-item"
        >
          [+5] Add 5 <span className="sr-only">table rows</span>
        </Button>
      </ButtonGroup>

      <FormValuesWatcher control={control} onValuesChange={handleValuesChanged} />

      {formState.isSubmitted && Object.keys(formState.errors).length > 0 && (
        <p className="text-right">
          <span className="text-danger">Please correct the errors above to continue.</span>
        </p>
      )}
      <GhgCalculatorNavigation handleSubmit={handleSubmit} />
    </div>
  )
}

const livestockClassOption = (clz: LivestockClass, clzType: LivestockClassType) => {
  // Make category name shorter so option doesn't appear truncated
  const trimmedCategoryName = clzType.name_display.replace(' Cattle', '')
  return (
    <option value={clz.id} key={clz.id}>
      {trimmedCategoryName} {clz.name}
    </option>
  )
}

/* ==================================================
 * ON FARM ITEM
 * =================================================
 */
const OnFarmGrazingItemRow = React.memo(
  (props: {
    control: Control
    initalValue: GrazingOnMovementItemFrontend
    fieldPrefix: string
    itemIdx: number
    livestockClasses: LivestockClass[]
    livestockClassTypes: LivestockClassType[]
    onRemove: (idx: number) => void
    seasonStart: Date
    seasonEnd: Date

    // these following props are mainly for the purpose of React.memo optimization
    // so the components get re-render properly when these props changed
    rowErrors: any // eslint-disable-line @typescript-eslint/no-explicit-any
    rowTouched: any // eslint-disable-line @typescript-eslint/no-explicit-any
    isSubmitted: boolean
  }) => {
    const { fieldPrefix, livestockClasses, livestockClassTypes, control, onRemove } = props
    const { seasonStart, seasonEnd, itemIdx, initalValue } = props

    const item: GrazingOnMovementItemFrontend | undefined = useWatch({
      control,
      name: fieldPrefix,
    })

    useEffect(() => {
      if (item?.on_hand_at_close) {
        control.setValue(`${fieldPrefix}.left_date`, null)
      }
      if (item?.on_hand_at_open) {
        control.setValue(`${fieldPrefix}.arrived_date`, null)
      }
      control.trigger([
        `${fieldPrefix}.tally_leave`,
        `${fieldPrefix}.tally_arrive`,
        `${fieldPrefix}.arrived_date`,
        `${fieldPrefix}.left_date`,
      ])
    }, [item?.on_hand_at_open, item?.on_hand_at_close, control, fieldPrefix]) //

    const timeOnFarm = useGrazingOnTimeOnFarmCalculation(item, seasonStart, seasonEnd)

    return (
      <tr data-testid="grazing-on-item">
        <td>
          <Controller
            name={`${fieldPrefix}.classification`}
            control={control}
            defaultValue={initalValue.classification}
            render={({ onChange, onBlur, value, name }) => (
              <Form.Control
                as="select"
                name={name}
                value={value || ''}
                onChange={(e) => onChange(parseInt(e.target.value))}
                onBlur={onBlur}
                className={fieldErrorCssClass(
                  control,
                  `${fieldPrefix}.classification`,
                  'is-invalid'
                )}
              >
                <option value="" disabled>
                  - Select class -
                </option>
                {livestockClassTypes.map((clzType) => (
                  <optgroup label={clzType.name_display} key={clzType.id}>
                    {livestockClasses
                      .filter((clz) => clz.livestock_type === clzType.id)
                      .map((clz) => livestockClassOption(clz, clzType))}
                  </optgroup>
                ))}
              </Form.Control>
            )}
          />
          <ErrorMessage control={control} name={`${fieldPrefix}.classification`} />
        </td>
        <td className="number bg-light">
          <NumberField
            name={`${fieldPrefix}.tally_arrive`}
            control={control}
            placeholder="0"
            defaultValue={initalValue.tally_arrive}
            maxLength={6}
            aria-label="Tally arrive"
          />
        </td>
        <td className="number bg-light">
          <NumberField
            name={`${fieldPrefix}.tally_leave`}
            control={control}
            placeholder="0"
            defaultValue={initalValue.tally_leave}
            maxLength={6}
            aria-label="Tally leave"
          />
        </td>
        <td className="bg-light text-center">
          <Controller
            name={`${fieldPrefix}.on_hand_at_open`}
            control={control}
            defaultValue={initalValue.on_hand_at_open}
            render={({ onChange, value }) => (
              <Form.Check
                id={`${fieldPrefix}.on_hand_at_open`}
                aria-label="On hand at open"
                checked={value}
                onChange={(e) => onChange(e.target.checked)}
                bsCustomPrefix="custom-control"
                custom
              />
            )}
          />
        </td>
        <td className="bg-light">
          <InputGroup>
            <Controller
              name={`${fieldPrefix}.arrived_date`}
              control={control}
              defaultValue={initalValue.arrived_date}
              render={({ onChange, onBlur, value }) => (
                <DateInput
                  isDateOnly
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  disabled={item?.on_hand_at_open}
                  wrapperClassname={fieldErrorCssClass(
                    control,
                    `${fieldPrefix}.arrived_date`,
                    'is-invalid'
                  )}
                  defaultValues={{
                    date: '0',
                    month: '0',
                    year: '0',
                  }}
                  data-testid="arrived-date-input"
                />
              )}
            />

            <ErrorMessage control={control} name={`${fieldPrefix}.arrived_date`} />
          </InputGroup>
        </td>
        <td className="bg-light text-center">
          <Controller
            name={`${fieldPrefix}.on_hand_at_close`}
            control={control}
            defaultValue={initalValue.on_hand_at_close}
            render={({ onChange, value }) => (
              <Form.Check
                id={`${fieldPrefix}.on_hand_at_close`}
                aria-label="On hand at close"
                checked={value}
                onChange={(e) => onChange(e.target.checked)}
                bsCustomPrefix="custom-control"
                custom
              />
            )}
          />
        </td>
        <td className="bg-light">
          <InputGroup>
            <Controller
              name={`${fieldPrefix}.left_date`}
              control={control}
              defaultValue={initalValue.left_date}
              render={({ onChange, onBlur, value }) => (
                <DateInput
                  isDateOnly
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  disabled={item?.on_hand_at_close}
                  wrapperClassname={fieldErrorCssClass(
                    control,
                    `${fieldPrefix}.left_date`,
                    'is-invalid'
                  )}
                  defaultValues={{
                    date: '0',
                    month: '0',
                    year: '0',
                  }}
                  data-testid="left-date-input"
                />
              )}
            />
            <ErrorMessage control={control} name={`${fieldPrefix}.left_date`} />
          </InputGroup>
        </td>
        <td className="text-center" data-testid="livestock-time-on-farm">
          {formatPercentage(timeOnFarm)}
        </td>
        <td>
          <Button
            variant="outline-danger btn-icon"
            onClick={() => onRemove(itemIdx)}
            aria-label="Remove"
            title="Remove"
          >
            <FontAwesomeIcon icon={faTimesCircle} />
          </Button>
        </td>
      </tr>
    )
  },
  (prevProps, nextProps) =>
    isEqual(nextProps.rowErrors, prevProps.rowErrors) &&
    isEqual(nextProps.rowTouched, prevProps.rowTouched) &&
    nextProps.fieldPrefix === prevProps.fieldPrefix &&
    nextProps.onRemove === prevProps.onRemove &&
    nextProps.isSubmitted === prevProps.isSubmitted
)

/* ==================================================
 * OFF FARM ITEM
 * =================================================
 */
const OffFarmGrazingItemRow = React.memo(
  (props: {
    control: Control
    itemIdx: number
    initalValue: GrazingOffMovementItemFrontend
    fieldPrefix: string
    livestockClasses: LivestockClass[]
    livestockClassTypes: LivestockClassType[]
    onRemove: (idx: number) => void
    seasonStart: Date
    seasonEnd: Date

    // these following props are mainly for the purpose of React.memo optimization
    // so the components get re-render properly when these props changed
    rowErrors: any // eslint-disable-line @typescript-eslint/no-explicit-any
    rowTouched: any // eslint-disable-line @typescript-eslint/no-explicit-any
    isSubmitted: boolean
  }) => {
    const { fieldPrefix, livestockClasses, livestockClassTypes } = props
    const { control, onRemove, itemIdx, initalValue, seasonStart, seasonEnd } = props

    const item: GrazingOffMovementItemFrontend | undefined = useWatch({
      control,
      name: fieldPrefix,
    })

    useEffect(() => {
      if (item?.on_hand_at_open) {
        control.setValue(`${fieldPrefix}.departed_date`, '')
      }
      if (item?.on_hand_at_close) {
        control.setValue(`${fieldPrefix}.returned_date`, '')
      }

      control.trigger([
        `${fieldPrefix}.tally_depart`,
        `${fieldPrefix}.tally_return`,
        `${fieldPrefix}.departed_date`,
        `${fieldPrefix}.returned_date`,
      ])
    }, [item?.on_hand_at_open, item?.on_hand_at_close, control, fieldPrefix])

    const timeOnFarm = useGrazingOffTimeOnFarmCalculation(item, seasonStart, seasonEnd)

    return (
      <tr data-testid="grazing-off-item">
        <td>
          <Controller
            name={`${fieldPrefix}.classification`}
            control={control}
            defaultValue={initalValue.classification}
            render={({ onChange, onBlur, value, name }) => (
              <Form.Control
                as="select"
                name={name}
                value={value || ''}
                onChange={(e) => onChange(parseInt(e.target.value))}
                onBlur={onBlur}
                className={fieldErrorCssClass(
                  control,
                  `${fieldPrefix}.classification`,
                  'is-invalid'
                )}
              >
                <option value="" disabled>
                  - Select class -
                </option>
                {livestockClassTypes.map((clzType) => (
                  <optgroup label={clzType.name_display} key={clzType.id}>
                    {livestockClasses
                      .filter((clz) => clz.livestock_type === clzType.id)
                      .map((clz) => livestockClassOption(clz, clzType))}
                  </optgroup>
                ))}
              </Form.Control>
            )}
          />
          <ErrorMessage control={control} name={`${fieldPrefix}.classification`} />
        </td>
        <td className="number bg-light">
          <NumberField
            name={`${fieldPrefix}.tally_depart`}
            control={control}
            placeholder="0"
            defaultValue={initalValue.tally_depart}
            maxLength={6}
            aria-label="Tally depart"
          />
        </td>
        <td className="number bg-light">
          <NumberField
            name={`${fieldPrefix}.tally_return`}
            control={control}
            placeholder="0"
            defaultValue={initalValue.tally_return}
            maxLength={6}
            aria-label="Tally return"
          />
        </td>
        <td className="bg-light text-center">
          <Controller
            name={`${fieldPrefix}.on_hand_at_open`}
            control={control}
            defaultValue={initalValue.on_hand_at_open}
            render={({ onChange, value }) => (
              <Form.Check
                id={`${fieldPrefix}.on_hand_at_open`}
                aria-label="Off farm at open"
                checked={value}
                onChange={(e) => onChange(e.target.checked)}
                bsCustomPrefix="custom-control custom-checkbox"
                custom
              />
            )}
          />
        </td>
        <td className="bg-light">
          <InputGroup>
            <Controller
              name={`${fieldPrefix}.departed_date`}
              control={control}
              defaultValue={initalValue.departed_date}
              render={({ onChange, onBlur, value }) => (
                <DateInput
                  isDateOnly
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  disabled={item?.on_hand_at_open}
                  wrapperClassname={fieldErrorCssClass(
                    control,
                    `${fieldPrefix}.departed_date`,
                    'is-invalid'
                  )}
                  data-testid="departed-date-input"
                />
              )}
            />
            <ErrorMessage control={control} name={`${fieldPrefix}.departed_date`} />
          </InputGroup>
        </td>
        <td className="bg-light text-center">
          <Controller
            name={`${fieldPrefix}.on_hand_at_close`}
            control={control}
            defaultValue={initalValue.on_hand_at_close}
            render={({ onChange, value }) => (
              <Form.Check
                id={`${fieldPrefix}.on_hand_at_close`}
                aria-label="Off farm at close"
                checked={value}
                onChange={(e) => onChange(e.target.checked)}
                bsCustomPrefix="custom-control custom-checkbox"
                custom
              />
            )}
          />
        </td>
        <td className="bg-light">
          <InputGroup>
            <Controller
              name={`${fieldPrefix}.returned_date`}
              control={control}
              defaultValue={initalValue.returned_date}
              render={({ onChange, onBlur, value }) => (
                <DateInput
                  isDateOnly
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  disabled={item?.on_hand_at_close}
                  wrapperClassname={fieldErrorCssClass(
                    control,
                    `${fieldPrefix}.returned_date`,
                    'is-invalid'
                  )}
                  data-testid="returned-date-input"
                />
              )}
            />
            <ErrorMessage control={control} name={`${fieldPrefix}.returned_date`} />
          </InputGroup>
        </td>
        <td className="text-center" data-testid="livestock-time-on-farm">
          {formatPercentage(timeOnFarm)}
        </td>
        <td>
          <Button
            variant="outline-danger btn-icon"
            onClick={() => onRemove(itemIdx)}
            aria-label="Remove"
            title="Remove"
          >
            <FontAwesomeIcon icon={faTimesCircle} />
          </Button>
        </td>
      </tr>
    )
  },
  (prevProps, nextProps) =>
    isEqual(nextProps.rowErrors, prevProps.rowErrors) &&
    isEqual(nextProps.rowTouched, prevProps.rowTouched) &&
    nextProps.fieldPrefix === prevProps.fieldPrefix &&
    nextProps.onRemove === prevProps.onRemove &&
    nextProps.isSubmitted === prevProps.isSubmitted
)
